import React from 'react';
import { useChat } from '../contexts/ChatContext';
import { useAuth } from '../contexts/AuthContext';
import { X, MessageCircle, Send, Loader, Trash2 } from 'lucide-react';
import './ChatWidget.css';

const ChatWidget: React.FC = () => {
  const {
    messages,
    isOpen,
    isTyping,
    error,
    isAuthenticated,
    sendMessage,
    toggleChat,
    closeChat,
    resetChat
  } = useChat();

  const { user } = useAuth();

  const [input, setInput] = React.useState('');
  const messagesEndRef = React.useRef<HTMLDivElement>(null);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const isSubmittingRef = React.useRef(false);
  const widgetRef = React.useRef<HTMLDivElement>(null);

  // =========================
  // SCROLL TO BOTTOM
  // =========================
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // =========================
  // AUTO FOCUS INPUT
  // =========================
  React.useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // =========================
  // CLICK OUTSIDE TO CLOSE
  // =========================
  React.useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        isOpen &&
        widgetRef.current &&
        !widgetRef.current.contains(e.target as Node)
      ) {
        closeChat();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, closeChat]);

  // =========================
  // SEND MESSAGE
  // =========================
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isSubmittingRef.current || isTyping) return;
    if (!input.trim()) return;

    isSubmittingRef.current = true;

    const text = input.trim();
    setInput('');

    try {
      await sendMessage(text);
    } finally {
      isSubmittingRef.current = false;
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e as any);
    }

    if (e.key === 'Escape') {
      closeChat();
    }
  };

  // =========================
  // CLOSED STATE (FAB)
  // =========================
  if (!isOpen) {
    return (
      <button
        className="chat-fab"
        onClick={toggleChat}
        aria-label="Open chat"
      >
        <MessageCircle size={22} />
      </button>
    );
  }

  // =========================
  // OPEN CHAT UI
  // =========================
  return (
    <div
      ref={widgetRef}
      className="chat-widget chat-open"
      role="dialog"
      aria-label="AI Chat Assistant"
    >
      {/* HEADER */}
      <div className="chat-header">
        <div>
          <h3>EmailAI Assistant</h3>
          <p className="sub">
            {isAuthenticated ? `Welcome, ${user?.name}` : 'Guest mode'}
          </p>
        </div>

        <div className="actions">
          <button onClick={resetChat} aria-label="Clear chat">
            <Trash2 size={16} />
          </button>
          <button onClick={closeChat} aria-label="Close chat">
            <X size={16} />
          </button>
        </div>
      </div>

      {/* MESSAGES */}
      <div className="chat-body">
        {messages.length === 0 && (
          <div className="typing">Start a conversation...</div>
        )}

        {messages.map((m) => (
          <div
            key={m.id}
            className={`msg ${m.sender === 'user' ? 'user' : 'ai'}`}
          >
            {m.content}
          </div>
        ))}

        {isTyping && (
          <div className="typing">
            <Loader className="spin" size={16} />
            <span>Thinking...</span>
          </div>
        )}

        {error && <div className="error">{error}</div>}

        <div ref={messagesEndRef} />
      </div>

      {/* INPUT */}
      <form className="chat-input" onSubmit={handleSubmit}>
        <input
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isTyping ? 'AI is responding...' : 'Type message...'}
          disabled={isTyping}
        />

        <button type="submit" disabled={!input.trim() || isTyping}>
          {isTyping ? <Loader size={16} className="spin" /> : <Send size={16} />}
        </button>
      </form>
    </div>
  );
};

export default ChatWidget;